/**
 * Where we have just been: a rolling trail of the last 100 km driven.
 *
 * Drawn as a dotted line under everything else, so at a glance the driver can
 * see "we already came down this road" - useful when a delivery loops back on
 * itself, and when proving a road was driven at all. Unlike the trip log this
 * records in EVERY mode, not only while navigating: driving around without a
 * destination still leaves the trail.
 *
 * Storage is localStorage rather than IndexedDB: 100 km at one point per 30 m
 * is ~3300 coordinate pairs, well under 100 kB as JSON, and the write is
 * throttled. Runs are split when the GPS teleports (tunnel, app closed and
 * reopened elsewhere) so the map never draws a straight dot-line across the
 * province between two sessions.
 */

import type { Fix, LngLat } from '../core/types';
import { distance } from '../core/geo';

/** Total trail length kept, metres. */
const MAX_METRES = 100_000;
/** Don't record a new dot closer than this to the last one. */
const MIN_STEP_M = 30;
/** A jump further than this starts a new run instead of joining the line. */
const GAP_M = 1500;
/** A fix this inaccurate is not evidence of where the vehicle went. */
const MAX_ACCURACY_M = 60;
/** Persist at most this often. */
const SAVE_EVERY_MS = 30_000;

const KEY = 'wayfarer.breadcrumbs.v1';

interface Stored {
  /** Runs of consecutive driving; each run is a polyline. */
  runs: Array<Array<[number, number]>>;
}

export class BreadcrumbTrail {
  private runs: LngLat[][] = [];
  /** Metres in each run, kept alongside so trimming needn't re-measure. */
  private runMetres: number[] = [];
  private total = 0;
  private lastSavedAt = 0;
  private dirty = false;

  constructor() {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const stored = JSON.parse(raw) as Stored;
        if (Array.isArray(stored?.runs)) {
          this.runs = stored.runs.filter((r) => Array.isArray(r) && r.length > 0) as LngLat[][];
          for (const run of this.runs) {
            let m = 0;
            for (let i = 1; i < run.length; i++) m += distance(run[i - 1], run[i]);
            this.runMetres.push(m);
            this.total += m;
          }
        }
      }
    } catch {
      /* corrupt or absent - start clean */
    }
    // The gap between last session and this one is always a new run.
    this.runs.push([]);
    this.runMetres.push(0);

    // localStorage writes are cheap but not free; flush on the way out too so
    // the last minute of a drive is not lost to the throttle.
    const flush = () => this.save(true);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  /** Feed every GPS fix; the trail decides what is worth keeping. */
  add(fix: Fix): boolean {
    if (fix.accuracy > MAX_ACCURACY_M) return false;
    const here: LngLat = [round5(fix.lng), round5(fix.lat)];
    const run = this.runs[this.runs.length - 1];
    const last = run.length ? run[run.length - 1] : null;

    if (!last) {
      run.push(here);
      this.dirty = true;
      return true;
    }

    const moved = distance(last, here);
    if (moved < MIN_STEP_M) return false;

    if (moved > GAP_M) {
      this.runs.push([here]);
      this.runMetres.push(0);
    } else {
      run.push(here);
      this.runMetres[this.runMetres.length - 1] += moved;
      this.total += moved;
    }
    this.trim();
    this.dirty = true;
    this.save(false);
    return true;
  }

  /** Drop the oldest ground until the trail is back inside its 100 km. */
  private trim(): void {
    while (this.total > MAX_METRES && this.runs.length) {
      const first = this.runs[0];
      if (first.length < 2) {
        this.total -= this.runMetres.shift() ?? 0;
        this.runs.shift();
        continue;
      }
      const step = distance(first[0], first[1]);
      first.shift();
      this.runMetres[0] -= step;
      this.total -= step;
    }
  }

  /** Polylines for drawing. Single-point runs are invisible; skip them. */
  lines(): LngLat[][] {
    return this.runs.filter((r) => r.length >= 2);
  }

  private save(force: boolean): void {
    if (!this.dirty) return;
    const now = Date.now();
    if (!force && now - this.lastSavedAt < SAVE_EVERY_MS) return;
    this.lastSavedAt = now;
    this.dirty = false;
    try {
      const stored: Stored = { runs: this.runs.filter((r) => r.length > 0) };
      localStorage.setItem(KEY, JSON.stringify(stored));
    } catch {
      /* quota / private mode - the trail just won't survive a reload */
    }
  }
}

function round5(n: number): number {
  return Math.round(n * 1e5) / 1e5;
}
